import { Divider, Drawer, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { IconButton } from "@itk/mui-components";
import CloseIcon from "@mui/icons-material/Close";
import { genericTranslationsK } from "src/translation-hooks/generic-translations";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { useProductInfo } from "src/pages/product-details/useProductInfo";
import { DekoxIcon } from "src/components";
import { useProjectsPageProductDetailsSectionT } from "src/translation-hooks/projects-page-product-details-section";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import parse from "html-react-parser";
import { PRODUCT_INFO_DIALOG_WIDTH } from "../../configurator-utils";
import { productInformationActions } from "./product-information-dialog-store";

const mapStateToProps = (state: RootState) => ({
  isProductInformationDialogOpen: state.pageReducer.combinedConfiguratorReducer.productInformationDialogReducer.isProductInformationDialogOpen
});

const ownActions = {
  setIsProductInformationDialogOpen: productInformationActions.setIsProductInformationDialogOpen
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ProductInformationDialogBase = (props: Props) => {
  const { isProductInformationDialogOpen, setIsProductInformationDialogOpen } = props;
  const theme = useTheme();
  const configuratorPageT = useConfiguratorPageT();
  const projectsPageProductDetailsSectionT = useProjectsPageProductDetailsSectionT();

  const { product } = useProductInfo({ productId: isProductInformationDialogOpen?.productId });

  return (
    <Drawer
      anchor="right"
      open
      onClose={() => {
        setIsProductInformationDialogOpen(undefined);
      }}
      sx={{
        "& .MuiDrawer-paper": {
          width: PRODUCT_INFO_DIALOG_WIDTH,
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      {!product && (
        <Stack flex={1} gap={2} px={2.5} py={2}>
          <Skeleton height={40} />
          <Skeleton height={30} />
          <Skeleton height={30} />
          <Skeleton height={30} />
          <Skeleton height={30} />
        </Stack>
      )}
      {product && (
        <>
          <Stack px={2.5} py={2} direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color={theme.palette.customColors.darkGrey}>
              {configuratorPageT.productInformation}
            </Typography>
            <IconButton k={genericTranslationsK.close} onClick={() => setIsProductInformationDialogOpen(undefined)} size="small" sx={{ color: theme.palette.customColors.darkGrey }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Divider />

          <Stack px={2.5} py={2} overflow="auto" flex={1}>
            <Typography variant="h2" color="customColors.darkGrey" mb={2}>
              {product.description} ({product.productNumber})
            </Typography>

            <Divider />

            <Stack gap={0} divider={<Divider />}>
              <Stack direction="row" gap={1.5} py={1.5} alignItems="flex-start">
                <DekoxIcon icon="category" sx={{ color: theme.palette.customColors.darkGrey, mt: 0.2 }} />
                <Stack>
                  <Typography variant="subtitle2" color="customColors.darkGrey">
                    {projectsPageProductDetailsSectionT.productCategory}
                  </Typography>
                  <Typography variant="subtitle1" color="customColors.darkGrey">
                    {product.productTypeName || "-"}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" gap={1.5} py={1.5} alignItems="flex-start">
                <DekoxIcon icon="expand" sx={{ color: theme.palette.customColors.darkGrey, mt: 0.2 }} />
                <Stack>
                  <Typography variant="subtitle2" color="customColors.darkGrey">
                    {projectsPageProductDetailsSectionT.width}
                  </Typography>
                  <Typography variant="subtitle1" color="customColors.darkGrey">
                    {product.productGroupCodeDescription || "-"}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" gap={1.5} py={1.5} alignItems="flex-start">
                <DekoxIcon icon="info" sx={{ color: theme.palette.customColors.darkGrey, mt: 0.2 }} />
                <Stack>
                  <Typography variant="subtitle2" color="customColors.darkGrey">
                    {projectsPageProductDetailsSectionT.sound}
                  </Typography>
                  <Typography variant="subtitle1" color="customColors.darkGrey">
                    {product.productCategoryCodeDescription || "-"}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            {product.summary && (
              <Stack gap={0.5} mt={1}>
                <Divider />
                <Stack py={1.5} gap={0.5}>
                  <Typography variant="subtitle2" color="customColors.darkGrey">
                    {projectsPageProductDetailsSectionT.productDescription}
                  </Typography>
                  <Typography variant="subtitle1" color="customColors.darkGrey" lineHeight={1.6}>
                    {parse(product.summary)}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </>
      )}
    </Drawer>
  );
};

export const ProductInformationDialog = connect(mapStateToProps, ownActions)(ProductInformationDialogBase);
